import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { useChartColors } from "@/lib/useChartColors";
import { TrendUp, ChartLineUp } from "@phosphor-icons/react";

const SEV_COLORS = { Critical: "#ef4444", High: "#f97316", Medium: "#f59e0b", Low: "#38bdf8" };

const Kpi = ({ label, value, hint }) => (
  <Card className="p-4 border-border rounded-sm" data-testid={`soc-trend-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
    <div className="font-display text-2xl font-bold">{value}</div>
    {hint && <div className="text-[10px] text-muted-foreground font-mono mt-1">{hint}</div>}
  </Card>
);

const Panel = ({ title, children, testid }) => (
  <Card className="p-5 border-border rounded-sm" data-testid={testid}>
    <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-4">{title}</div>
    <div style={{ width: "100%", height: 260 }}>{children}</div>
  </Card>
);

export default function SocTrends() {
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState("all");
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const chart = useChartColors();

  const load = useCallback(() => {
    api.get(`/soc/trends?client=${encodeURIComponent(client)}&days=${days}`).then(r => setData(r.data)).catch(() => {});
  }, [client, days]);

  useEffect(() => { api.get("/client-registry?module=soc").then(r => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-6 text-muted-foreground">Loading SOC trends…</div>;
  const s = data.summary || {};
  const axis = { stroke: chart.axis, fontSize: 11 };
  const tt = { contentStyle: chart.tooltip };

  return (
    <div className="p-6 space-y-5" data-testid="soc-trends-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-rag-red mb-1">Security Operations</div>
          <h1 className="font-display text-3xl font-black flex items-center gap-2"><TrendUp size={26} /> SOC Trends</h1>
          <p className="text-sm text-muted-foreground mt-1">Detection & response performance over time — {data.weekly ? "weekly" : "daily"} buckets.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[10px] font-mono uppercase text-muted-foreground">Client</label>
            <Select value={client} onValueChange={setClient}>
              <SelectTrigger data-testid="soc-trend-client" className="w-48 rounded-sm h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All clients</SelectItem>{clients.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-muted-foreground">Window</label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger data-testid="soc-trend-days" className="w-32 rounded-sm h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 180 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Kpi label="Total" value={s.total ?? 0} />
        <Kpi label="MTTD (h)" value={s.mttd_hours ?? 0} hint="detect" />
        <Kpi label="MTTA (h)" value={s.mtta_hours ?? 0} hint="acknowledge" />
        <Kpi label="MTTC (h)" value={s.mttc_hours ?? 0} hint="contain" />
        <Kpi label="MTTR (h)" value={s.mttr_hours ?? 0} hint="resolve" />
        <Kpi label="TP:FP" value={`${s.tp ?? 0}:${s.fp ?? 0}`} hint={`ratio ${s.tp_fp_ratio ?? 0}`} />
        <Kpi label="SLA Breached" value={s.resolution_breached ?? 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Case volume by severity" testid="soc-trend-volume">
          <ResponsiveContainer>
            <BarChart data={data.volume_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
              <XAxis dataKey="date" {...axis} tickFormatter={d => (d || "").slice(5)} minTickGap={24} />
              <YAxis {...axis} allowDecimals={false} />
              <Tooltip {...tt} />
              <Bar dataKey="Low" stackId="a" fill={SEV_COLORS.Low} />
              <Bar dataKey="Medium" stackId="a" fill={SEV_COLORS.Medium} />
              <Bar dataKey="High" stackId="a" fill={SEV_COLORS.High} />
              <Bar dataKey="Critical" stackId="a" fill={SEV_COLORS.Critical} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="MTTD / MTTA / MTTC / MTTR (hours)" testid="soc-trend-mtt">
          <ResponsiveContainer>
            <LineChart data={data.mtt_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
              <XAxis dataKey="bucket" {...axis} minTickGap={24} />
              <YAxis {...axis} />
              <Tooltip {...tt} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="mttd" name="MTTD" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="mtta" name="MTTA" stroke="#a78bfa" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="mttc" name="MTTC" stroke="#f97316" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="mttr" name="MTTR" stroke="#34d399" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="True vs False Positive" testid="soc-trend-tpfp">
          <ResponsiveContainer>
            <BarChart data={data.tpfp_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
              <XAxis dataKey="bucket" {...axis} minTickGap={24} />
              <YAxis {...axis} allowDecimals={false} />
              <Tooltip {...tt} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="tp" name="True Positive" fill="#ef4444" />
              <Bar dataKey="fp" name="False Positive" fill="#64748b" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="SLA breaches (resolution)" testid="soc-trend-breach">
          <ResponsiveContainer>
            <LineChart data={data.breach_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
              <XAxis dataKey="date" {...axis} tickFormatter={d => (d || "").slice(5)} minTickGap={24} />
              <YAxis {...axis} allowDecimals={false} />
              <Tooltip {...tt} />
              <Line type="monotone" dataKey="breached" name="Breached" stroke="#ef4444" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {client === "all" && data.per_client?.length > 0 && (
        <Card className="border-border rounded-sm overflow-hidden" data-testid="soc-trend-per-client">
          <div className="px-4 py-3 text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2 border-b border-border">
            <ChartLineUp size={14} /> Per-client breakdown
          </div>
          <table className="w-full text-sm">
            <thead className="bg-accent/30 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2.5">Client</th>
                <th className="text-right px-3 py-2.5">Cases</th>
                <th className="text-right px-3 py-2.5">MTTD (h)</th>
                <th className="text-right px-3 py-2.5">MTTA (h)</th>
                <th className="text-right px-3 py-2.5">MTTC (h)</th>
                <th className="text-right px-3 py-2.5">MTTR (h)</th>
                <th className="text-right px-3 py-2.5">TP:FP</th>
                <th className="text-right px-3 py-2.5">SLA Breached</th>
              </tr>
            </thead>
            <tbody>
              {data.per_client.map(r => (
                <tr key={r.client} className="border-t border-border" data-testid={`soc-trend-row-${r.client}`}>
                  <td className="px-3 py-2.5 font-medium">{r.client}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{r.total}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{r.mttd_hours}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{r.mtta_hours}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{r.mttc_hours}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{r.mttr_hours}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{r.tp}:{r.fp}</td>
                  <td className={`px-3 py-2.5 text-right font-mono ${r.resolution_breached ? "text-rag-red-fg" : ""}`}>{r.resolution_breached}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 border-border rounded-sm" data-testid="soc-trend-by-source">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Alert Volume by Source</div>
          {(data.volume_by_source || []).map(r => (
            <div key={r.source} className="flex items-center gap-3 text-sm mb-1.5">
              <span className="w-24 shrink-0">{r.source}</span>
              <div className="flex-1 h-2 bg-accent/40 rounded-full overflow-hidden"><div className="h-full bg-primary" style={{ width: `${(r.count / (data.volume_by_source[0]?.count || 1)) * 100}%` }} /></div>
              <span className="font-mono text-xs w-8 text-right">{r.count}</span>
            </div>
          ))}
          {(data.volume_by_source || []).length === 0 && <div className="text-xs text-muted-foreground">No data.</div>}
        </Card>
        <Card className="p-5 border-border rounded-sm" data-testid="soc-trend-top-assets">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Top Targeted Assets</div>
          {(data.top_assets || []).map(r => (
            <div key={r.asset} className="flex items-center gap-3 text-sm mb-1.5">
              <span className="flex-1 truncate font-mono text-xs">{r.asset}</span>
              <span className="font-mono text-xs w-8 text-right">{r.count}</span>
            </div>
          ))}
          {(data.top_assets || []).length === 0 && <div className="text-xs text-muted-foreground">No affected-asset data captured on cases.</div>}
        </Card>
      </div>
    </div>
  );
}
